import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface DocStatus {
  accepted_version: string | null;
  current_version: string | null;
  needs_acceptance: boolean;
}

interface AcceptanceStatus {
  privacy: DocStatus;
  terms: DocStatus;
  medical_disclaimer: DocStatus;
}

type DocKey = "privacy" | "terms" | "medical_disclaimer";

const DOC_LINKS: Record<DocKey, string> = {
  privacy: "/privacy",
  terms: "/terms",
  medical_disclaimer: "/medical-disclaimer",
};

function authHeaders(): Record<string, string> {
  try {
    const accessToken = localStorage.getItem("auth_access_token");
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  } catch {
    return {};
  }
}

async function fetchAcceptanceStatus(): Promise<AcceptanceStatus | null> {
  try {
    const res = await fetch("/api/v1/legal/me/acceptance-status/", {
      credentials: "include",
      headers: { Accept: "application/json", ...authHeaders() },
    });
    if (!res.ok) return null;
    return res.json() as Promise<AcceptanceStatus>;
  } catch {
    return null;
  }
}

async function postAcceptance(versions: Record<string, string>): Promise<boolean> {
  try {
    const res = await fetch("/api/v1/legal/me/accept/", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify(versions),
    });
    return res.ok;
  } catch {
    return false;
  }
}

interface LegalAcceptanceGateProps {
  isAuthenticated: boolean;
  onSignOut: () => void;
  children: ReactNode;
}

export function LegalAcceptanceGate({
  isAuthenticated,
  onSignOut,
  children,
}: LegalAcceptanceGateProps) {
  const { t } = useTranslation("legal");
  const [status, setStatus] = useState<AcceptanceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState<Partial<Record<DocKey, boolean>>>({});

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus(null);
      return;
    }
    setLoading(true);
    fetchAcceptanceStatus()
      .then(setStatus)
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const pendingDocs = status
    ? (Object.entries(status) as [DocKey, DocStatus][]).filter(
        ([, v]) => v.needs_acceptance
      )
    : [];

  const allChecked =
    pendingDocs.length > 0 && pendingDocs.every(([k]) => !!checked[k]);

  const handleAccept = useCallback(async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    const versions: Record<string, string> = {};
    for (const [key, val] of pendingDocs) {
      if (val.current_version) {
        versions[`${key}_version`] = val.current_version;
      }
    }
    const ok = await postAcceptance(versions);
    if (ok) setStatus(null);
    setSubmitting(false);
  }, [allChecked, submitting, pendingDocs]);

  if (!isAuthenticated || loading || !status || pendingDocs.length === 0) {
    return <>{children}</>;
  }

  const perDocLabel: Record<DocKey, string> = {
    privacy: t("gate.perDocLabel.privacy"),
    terms: t("gate.perDocLabel.terms"),
    medical_disclaimer: t("gate.perDocLabel.medical_disclaimer"),
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-gate-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
      >
        <div className="w-full max-w-lg rounded-xl border bg-card shadow-xl p-6 space-y-6">
          <div className="space-y-2">
            <h2
              id="legal-gate-title"
              className="text-xl font-semibold text-card-foreground"
            >
              {t("gate.title")}
            </h2>
            <p className="text-sm text-muted-foreground">{t("gate.description")}</p>
          </div>

          <div className="space-y-4">
            {pendingDocs.map(([key, val]) => (
              <div key={key} className="space-y-1">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id={`legal-accept-${key}`}
                    checked={!!checked[key]}
                    onChange={(e) =>
                      setChecked((prev) => ({ ...prev, [key]: e.target.checked }))
                    }
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary cursor-pointer"
                  />
                  <label
                    htmlFor={`legal-accept-${key}`}
                    className="text-sm text-card-foreground cursor-pointer"
                  >
                    {perDocLabel[key]}
                    {val.current_version && (
                      <span className="text-muted-foreground ms-1">
                        ({t("common.version")} {val.current_version})
                      </span>
                    )}
                  </label>
                </div>
                <a
                  href={DOC_LINKS[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-7 inline-block text-xs text-primary hover:underline"
                >
                  {t("gate.readMore")}
                </a>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={!allChecked || submitting}
              onClick={() => void handleAccept()}
              className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
            >
              {t("gate.ctaAccept")}
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="w-full rounded-lg border px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              {t("gate.ctaDecline")}
            </button>
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="pointer-events-none select-none">
        {children}
      </div>
    </>
  );
}
